
import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from "sonner";
import { getStreakMedal, getStreakMessage } from '@/utils/gamification';
import analytics from '@/utils/analytics';
import { UserProgress } from '@/types/user';
import { useAuth } from './AuthContext';

interface ProgressContextType {
  userProgress: UserProgress;
  completeDay: (day: number, points: number) => void;
}

// Initial progress state
const initialProgress: UserProgress = {
  currentDay: 1,
  completedDays: [],
  streak: 0,
  points: 0,
  medals: {
    bronze: 0,
    silver: 0,
    gold: 0,
  }
};

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

export const ProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [userProgress, setUserProgress] = useState<UserProgress>(initialProgress);

  // Load progress from localStorage
  useEffect(() => {
    try {
      // Load progress from localStorage
      const savedProgress = localStorage.getItem('userProgress');
      if (savedProgress) {
        try {
          const parsed = JSON.parse(savedProgress);
          // Convert lastCompletedAt string back to Date if it exists
          if (parsed.lastCompletedAt) {
            parsed.lastCompletedAt = new Date(parsed.lastCompletedAt);
          }
          setUserProgress(parsed);
        } catch (parseError) {
          console.error("Error parsing saved progress:", parseError);
          toast.error("Could not load your saved progress. Starting fresh.");
          setUserProgress(initialProgress);
        }
      }
    } catch (error) {
      console.error("Error loading progress:", error);
    }
  }, [user]);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('userProgress', JSON.stringify(userProgress));
    }
  }, [userProgress, user]);

  // Complete a day and update progress
  const completeDay = (day: number, points: number) => {
    try {
      setUserProgress(prev => {
        // Check if this day was already completed
        if (prev.completedDays.includes(day)) {
          return prev; // Don't update if already completed
        }

        const now = new Date();
        const lastCompleted = prev.lastCompletedAt;
        
        // Calculate new streak
        let newStreak = prev.streak;
        if (!lastCompleted) {
          // First completion
          newStreak = 1;
        } else {
          const lastCompletedDate = new Date(lastCompleted);
          const daysSinceLastCompletion = Math.floor(
            (now.getTime() - lastCompletedDate.getTime()) / (1000 * 60 * 60 * 24)
          );
          
          // Check if the last completion was yesterday or today
          const isToday = daysSinceLastCompletion === 0;
          const isYesterday = daysSinceLastCompletion === 1;
          
          if (isToday) {
            // If completed another lesson today, maintain streak
            newStreak = prev.streak;
          } else if (isYesterday) {
            // If completed yesterday, increment streak
            newStreak += 1;
          } else {
            // Broke streak, start over
            newStreak = 1;
            // Inform the user that their streak was reset
            if (prev.streak > 1) {
              toast.info(`Your ${prev.streak}-day streak was reset. Starting a new streak today!`);
            }
          }
        }
        
        // Calculate new medals based on streak milestones
        const newMedals = { ...prev.medals };
        const currentMedal = getStreakMedal(newStreak);
        
        // Award new medals at specific milestones
        if (newStreak === 7 && currentMedal.name === 'Bronze') {
          newMedals.bronze += 1;
        } else if (newStreak === 14 && currentMedal.name === 'Silver') {
          newMedals.silver += 1;
        } else if (newStreak === 30 && currentMedal.name === 'Platinum') {
          newMedals.gold += 1;
        }
        
        // Show streak achievement message for significant milestones
        if (newStreak > prev.streak && (newStreak === 3 || newStreak === 5 || newStreak === 7 || 
            newStreak === 10 || newStreak === 14 || newStreak === 21 || newStreak === 30)) {
          toast.success(getStreakMessage(newStreak));
        }
        
        // Calculate next day (use max to ensure progression)
        const nextDay = Math.max(day + 1, prev.currentDay + 1);
        
        // Update user progress
        return {
          ...prev,
          currentDay: nextDay > 30 ? 30 : nextDay, // Cap at 30
          completedDays: [...prev.completedDays, day].sort((a, b) => a - b),
          streak: newStreak,
          points: prev.points + points,
          lastCompletedAt: now,
          medals: newMedals
        };
      });
    } catch (error) {
      console.error("Error completing day:", error);
      toast.error("Failed to update your progress. Please try again.");
      throw error; // Rethrow to let calling component know there was an error
    }
  };

  return (
    <ProgressContext.Provider
      value={{
        userProgress,
        completeDay
      }}
    >
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (context === undefined) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};
